import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  API_BASE_URL,
  DivineServiceClient,
  PaginatedResponseOfDivineServiceListDto,
  DivineServiceDetailDto,
  CreateDivineServiceRequest,
  UpdateDivineServiceRequest,
  PaginatedResponseOfMemberAttendanceDto,
  AttendanceDto,
  BulkAttendanceDto,
  OrganizationUnitDto,
} from '../generated/api-client';
import { AppTelemetryService } from '../../observability/app-telemetry.service';

export {
  PaginatedResponseOfDivineServiceListDto,
  DivineServiceListDto,
  DivineServiceDetailDto,
  CreateDivineServiceRequest,
  UpdateDivineServiceRequest,
  PaginatedResponseOfMemberAttendanceDto,
  MemberAttendanceDto,
  AttendanceDto,
  BulkAttendanceDto,
  OrganizationUnitDto,
} from '../generated/api-client';

/**
 * Thin wrapper over the NSwag-generated DivineServiceClient.
 * Feature services should inject this class instead of DivineServiceClient directly.
 */
@Injectable({ providedIn: 'root' })
export class DivineServiceApiService {
  constructor(
    private readonly client: DivineServiceClient,
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string,
    private readonly telemetry: AppTelemetryService,
  ) {}

  // ========== DIVINE SERVICES ==========

  getDivineServices(
    unitId?: string | null,
    startDate?: string | null,
    endDate?: string | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    page?: number,
    pageSize?: number,
    searchTerm?: string | null,
    divineServiceTypeId?: number | null,
  ): Observable<PaginatedResponseOfDivineServiceListDto> {
    let params = new HttpParams();
    if (unitId) params = params.set('unitId', unitId);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    if (sortBy) params = params.set('sortBy', sortBy);
    if (sortDirection) params = params.set('sortDirection', sortDirection);
    if (page != null) params = params.set('page', page);
    if (pageSize != null) params = params.set('pageSize', pageSize);
    if (searchTerm) params = params.set('searchTerm', searchTerm);
    if (divineServiceTypeId != null) params = params.set('divineServiceTypeId', divineServiceTypeId);

    return this.telemetry.trackOperation(
      this.http.get<PaginatedResponseOfDivineServiceListDto>(`${this.baseUrl}/api/divine-services`, { params }),
      {
        successEventName: 'divine_services.list.loaded',
        failureEventName: 'divine_services.list.failed',
        attributes: {
          unitId,
          page,
          pageSize,
          sortBy,
          sortDirection,
        },
      },
    );
  }

  getDivineServiceById(id: string): Observable<DivineServiceDetailDto> {
    return this.http.get<DivineServiceDetailDto>(
      `${this.baseUrl}/api/divine-services/${encodeURIComponent(id)}`
    );
  }

  createDivineService(request: CreateDivineServiceRequest): Observable<DivineServiceDetailDto> {
    return this.telemetry.trackOperation(
      this.client.createDivineService(request),
      {
        successEventName: 'divine_service.created',
        attributes: {
          unitId: request.organizationUnitId,
        },
        successAttributes: response => ({
          divineServiceId: response.id,
        }),
      },
    );
  }

  updateDivineService(id: string, request: UpdateDivineServiceRequest): Observable<DivineServiceDetailDto> {
    return this.telemetry.trackOperation(
      this.client.updateDivineService(id, request),
      {
        successEventName: 'divine_service.updated',
        attributes: {
          divineServiceId: id,
          unitId: request.organizationUnitId,
        },
      },
    );
  }

  deleteDivineService(id: string): Observable<void> {
    return this.telemetry.trackOperation(
      this.client.deleteDivineService(id),
      {
        successEventName: 'divine_service.deleted',
        attributes: {
          divineServiceId: id,
        },
      },
    );
  }

  closeDivineService(id: string): Observable<void> {
    return this.telemetry.trackOperation(
      this.http.post<void>(`${this.baseUrl}/api/divine-services/${encodeURIComponent(id)}/close`, {}),
      {
        successEventName: 'divine_service.closed',
        attributes: {
          divineServiceId: id,
        },
      },
    );
  }

  getManageableUnits(): Observable<OrganizationUnitDto[]> {
    return this.client.getManageableUnits();
  }

  // ========== ATTENDANCE ==========

  getMembersWithAttendance(
    divineServiceId: string,
    page?: number,
    pageSize?: number,
  ): Observable<PaginatedResponseOfMemberAttendanceDto> {
    let params = new HttpParams();
    if (page !== undefined) {
      params = params.set('page', page);
    }
    if (pageSize !== undefined) {
      params = params.set('pageSize', pageSize);
    }

    return this.http.get<PaginatedResponseOfMemberAttendanceDto>(
      `${this.baseUrl}/api/divine-services/${encodeURIComponent(divineServiceId)}/members-attendance`,
      { params },
    );
  }

  getAttendance(
    divineServiceId: string,
    page?: number,
    pageSize?: number,
  ): Observable<AttendanceDto> {
    return this.client.getAttendance(divineServiceId, page, pageSize);
  }

  markAttendance(divineServiceId: string, memberId: string): Observable<void> {
    return this.client.markAttendance(divineServiceId, memberId);
  }

  unmarkAttendance(divineServiceId: string, memberId: string): Observable<void> {
    return this.client.unmarkAttendance(divineServiceId, memberId);
  }

  bulkMarkAttendance(divineServiceId: string, request: BulkAttendanceDto): Observable<void> {
    return this.telemetry.trackOperation(
      this.client.bulkMarkAttendance(divineServiceId, request),
      {
        successEventName: 'attendance.bulk_marked',
        attributes: {
          divineServiceId,
          personCount: request.personIds?.length ?? 0,
        },
      },
    );
  }

  bulkUnmarkAttendance(divineServiceId: string, request: BulkAttendanceDto): Observable<void> {
    return this.telemetry.trackOperation(
      this.client.bulkUnmarkAttendance(divineServiceId, request),
      {
        successEventName: 'attendance.bulk_unmarked',
        attributes: {
          divineServiceId,
          personCount: request.personIds?.length ?? 0,
        },
      },
    );
  }
}
